// src/modules/competition/hooks/useCompetitionData.js
import { useState, useEffect, useCallback } from 'react';
import { useService } from '../../../shared/hooks/useService';

const competitionCache = new Map(); // competitionId -> { data, fetchedAt }
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getCached = (competitionId) => {
    const entry = competitionId ? competitionCache.get(competitionId) : null;
    return entry && Date.now() - entry.fetchedAt < CACHE_TTL_MS ? entry.data : null;
};

export const useCompetitionData = (competitionId) => {
    // Initialize directly from cache so navigating back shows data instantly.
    const [competition, setCompetition] = useState(() => getCached(competitionId)?.competition ?? null);
    const [competitors, setCompetitors] = useState(() => getCached(competitionId)?.competitors ?? []);
    const [isLoading, setIsLoading] = useState(() => !getCached(competitionId));
    const [error, setError] = useState(null);
    const service = useService();

    // loadCompetitionData always fetches — used as reloadData after join to get fresh state.
    const loadCompetitionData = useCallback(async () => {
        try {
            setIsLoading(true);
            const data = await service.getCompetitionDetails(competitionId);

            if (!data || !data.competition) {
                throw new Error('Competition not found');
            }

            const cached = { competition: { ...data.competition }, competitors: data.competition.competitors || [] };
            competitionCache.set(competitionId, { data: cached, fetchedAt: Date.now() });
            setCompetition(cached.competition);
            setCompetitors(cached.competitors);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [competitionId, service]);

    useEffect(() => {
        // Cache hit — state already populated by lazy initializer.
        if (getCached(competitionId)) return;
        loadCompetitionData();
    }, [loadCompetitionData, competitionId]);

    return {
        competition,
        competitors,
        isLoading,
        error,
        reloadData: loadCompetitionData
    };
};
